import { io, type Socket } from 'socket.io-client';
import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from './auth';

let socket: Socket | null = null;
const rooms = new Map<string, number>();

/**
 * Realtime (§9): the server pushes change hints; we invalidate the affected TanStack queries and refetch
 * through the API, so permission checks always apply. On reconnect, everything active is refetched.
 */
export function connectSocket(qc: QueryClient) {
  const token = useAuth.getState().token;
  if (!token) return;
  if (socket) {
    socket.auth = { token };
    if (!socket.connected) socket.connect();
    return;
  }
  socket = io({ path: '/socket.io', auth: { token }, transports: ['websocket', 'polling'] });
  socket.on('connect', () => {
    for (const room of rooms.keys()) socket!.emit('subscribe', { room });
    void qc.invalidateQueries();
  });
  socket.on('item.changed', (e: { key?: string; projectId: string }) => {
    void qc.invalidateQueries({ queryKey: ['board'] });
    void qc.invalidateQueries({ queryKey: ['backlog', e.projectId] });
    void qc.invalidateQueries({ queryKey: ['items'] });
    if (e.key) void qc.invalidateQueries({ queryKey: ['item', e.key] });
  });
  socket.on('release.changed', () => void qc.invalidateQueries({ queryKey: ['release'] }));
  socket.on('notification', (n: { payload: { title?: string; key?: string }; type: string }) => {
    void qc.invalidateQueries({ queryKey: ['notifications'] });
    toast(labelFor(n.type), { description: [n.payload.key, n.payload.title].filter(Boolean).join(' · ') });
  });
  socket.on('run.changed', (e: { runId: string; buildId: string }) => {
    void qc.invalidateQueries({ queryKey: ['test-run', e.runId] });
    void qc.invalidateQueries({ queryKey: ['build-test-summary', e.buildId] });
  });
  // Messaging (FR-MSG-3..11): refetch the open channel/thread and sidebar unread counts.
  socket.on('message', (m: { channelId: string; threadParentId: string | null; id: string }) => {
    void qc.invalidateQueries({ queryKey: ['messages', m.channelId] });
    void qc.invalidateQueries({ queryKey: ['thread', m.threadParentId ?? m.id] });
    void qc.invalidateQueries({ queryKey: ['channels'] });
  });
  socket.on('channel.read', () => void qc.invalidateQueries({ queryKey: ['channels'] }));
  socket.on('retro.changed', (e: { sprintId: string }) => void qc.invalidateQueries({ queryKey: ['retro', e.sprintId] }));
  socket.on('timer', () => void qc.invalidateQueries({ queryKey: ['timer'] }));
  socket.on('note.changed', (e: { id: string }) => void qc.invalidateQueries({ queryKey: ['note', e.id] }));
  socket.on('auth_error', () => socket?.disconnect());
}

export function disconnectSocket() {
  socket?.disconnect();
  socket = null;
  rooms.clear();
}

/** Ref-counted room subscription (project:{id}, item:{key}, board:{id}). */
export function subscribeRoom(room: string) {
  rooms.set(room, (rooms.get(room) ?? 0) + 1);
  if (rooms.get(room) === 1) socket?.emit('subscribe', { room });
  return () => {
    const n = (rooms.get(room) ?? 1) - 1;
    if (n <= 0) {
      rooms.delete(room);
      socket?.emit('unsubscribe', { room });
    } else rooms.set(room, n);
  };
}

/** Listen to a raw realtime event (typing, presence); returns an unsubscribe function. */
export function onRealtime<T>(event: string, handler: (data: T) => void) {
  const s = socket;
  s?.on(event, handler as (...args: unknown[]) => void);
  return () => void s?.off(event, handler as (...args: unknown[]) => void);
}

/** Typing indicator for a joined room (throttled by the caller). */
export function emitTyping(room: string) {
  socket?.emit('typing', { room });
}

export function labelFor(type: string) {
  return (
    {
      assigned: 'Assigned to you',
      mentioned: 'You were mentioned',
      handoff: 'Handoff',
      triage_dev: 'Needs a developer',
      triage_qa: 'Needs a QA owner',
      note_assigned: 'Note assigned to you',
      note_assignment_updated: 'Note assignment updated',
      reminder: 'Reminder',
      watched_change: 'Watched item changed',
      comment: 'New comment',
      build_ready: 'Build ready for QA',
      document_ready: 'Document processed',
      media_ready: 'Recording ready',
      approval_request: 'Approval requested',
      approval_decided: 'Approval decided',
      timesheet_submitted: 'Timesheet to approve',
      timesheet_decided: 'Timesheet decision',
      timesheet_reminder: 'Timesheet reminder',
      triage_ageing: 'Ageing in triage',
      deputy_active: 'You are covering',
      automation: 'Automation',
      direct_message: 'Direct message',
      announcement: 'Announcement',
      digest: 'Your daily digest',
      ai_proposal: 'AI agent proposal to confirm',
      sprint_risk: 'Sprint off track',
      critical_escalation: 'Critical customer escalation',
    } as Record<string, string>
  )[type] ?? 'Notification';
}
